import React from "react";
import Box from "../boxes/Box";
import Button from "../controls/Button";
import DateField from "../form-fields/DateField";
import FormArrayItem, { FormArrayItemProps } from "../form-fields/FormArrayItem";
import SelectField from "../form-fields/SelectField";
import TextField from "../form-fields/TextField";
import { getCountryOptions, frequentlyChosenCountries } from "../../data/countries";
import { FieldArray, FieldArrayRenderProps } from "react-final-form-arrays";
import { PoliticallyExposedPerson, PoliticallyExposedPersonKind } from "../../types/user";
import { politicallyExposedPersonKinds } from "../../utils/user";
import { t } from "../../i18n";
import { jsonApi } from "../../api/utils";

const politicallyExposedPersonOptions = () => {
  return politicallyExposedPersonKinds.map((kind) => ({
    i18nKey: `loan.additional_data.pep_details.${kind}`,
    value: kind,
  }));
};

export const deleteField = async (id: number) => {
  return jsonApi.delete(`/internal_api/politically_exposed_persons/${id}`);
}

export const emptyPep: PoliticallyExposedPerson = {
  activityEndedOn: null,
  activityStartedOn: null,
  birthDate: null,
  birthPlaceCountryCode: "",
  id: null,
  idCode: "",
  firstName: "",
  kind: politicallyExposedPersonKinds[0],
  lastName: "",
  positionCountryCode: "EE",
  recentPosition: "",
};

interface PoliticallyExposedPersonFormProps extends FormArrayItemProps {
  kind: PoliticallyExposedPersonKind;
}

const PoliticallyExposedPersonForm = ({ index, kind, remove }: PoliticallyExposedPersonFormProps) => {
  const countriesOptions = getCountryOptions();
  const frequentlyChosenCountriesOptions = countriesOptions.filter(
    (country) => frequentlyChosenCountries.some(element => element === country.value)
  );

  return (
    <FormArrayItem
      index={index}
      idPrefix="politicallyExposedPerson"
      remove={remove}
    >
      <p className="mt-10 mb-5 font-semibold">
        {t("loan.additional_data.pep_details.title")}
      </p>
      <SelectField
        className="Form-field"
        i18nKey="loan.additional_data.pep_details.kind"
        id={`pep_${index}_kind`}
        name={`politicallyExposedPersons[${index}].kind`}
        options={politicallyExposedPersonOptions()}
      />
      {kind !== "personal" && (
        <>
          <Box className="DoubleFieldGroup">
            <TextField
              className="Form-field Form-field--280"
              i18key="loan.additional_data.pep_details.first_name"
              id={`pep_${index}_first_name`}
              name={`politicallyExposedPersons[${index}].firstName`}
            />
            <TextField
              className="Form-field Form-field--280"
              i18key="loan.additional_data.pep_details.last_name"
              id={`pep_${index}_last_name`}
              name={`politicallyExposedPersons[${index}].lastName`}
            />
          </Box>
          <TextField
            className="Form-field"
            i18key="loan.additional_data.pep_details.personal_code"
            id={`pep_${index}_id_code`}
            name={`politicallyExposedPersons[${index}].idCode`}
          />
          <Box className="DoubleFieldGroup">
            <DateField
              className="Form-field Form-field--280"
              i18nKey="loan.additional_data.pep_details.birth_date"
              id={`pep_${index}_birth_date`}
              name={`politicallyExposedPersons[${index}].birthDate`}
            />
            <SelectField
              className="Form-field"
              frequentlyChosenOptions={frequentlyChosenCountriesOptions}
              i18nKey="loan.additional_data.pep_details.birth_country"
              id={`pep_${index}_birth_place_country_code`}
              name={`politicallyExposedPersons[${index}].birthPlaceCountryCode`}
              options={[{name: "", value: ""}, ...countriesOptions]}
            />
          </Box>
        </>
      )}
      <Box className="DoubleFieldGroup">
        <SelectField
          className="Form-field"
          frequentlyChosenOptions={frequentlyChosenCountriesOptions}
          i18nKey="loan.additional_data.pep_details.position_country"
          id={`pep_${index}_position_country_code`}
          name={`politicallyExposedPersons[${index}].positionCountryCode`}
          options={countriesOptions}
        />
        <TextField
          className="Form-field Form-field--280"
          i18key="loan.additional_data.pep_details.recent_position"
          id={`pep_${index}_recent_position`}
          name={`politicallyExposedPersons[${index}].recentPosition`}
        />
      </Box>
      <Box className="DoubleFieldGroup">
        <DateField
          className="Form-field Form-field--280"
          i18nKey="loan.additional_data.pep_details.activity_started_on"
          id={`pep_${index}_politically_activity_started_on`}
          name={`politicallyExposedPersons[${index}].activityStartedOn`}
        />
        <DateField
          className="Form-field Form-field--280"
          i18nKey="loan.additional_data.pep_details.activity_ended_on"
          id={`pep_${index}_politically_activity_ended_on`}
          name={`politicallyExposedPersons[${index}].activityEndedOn`}
        />
      </Box>
    </FormArrayItem>
  );
};

const PoliticallyExposedPersonsFields = () => {
  return (
    <FieldArray name="politicallyExposedPersons">
      {({fields}: FieldArrayRenderProps<PoliticallyExposedPerson, HTMLElement>) => (
        <>
          <div>
            {fields.value.map((field, i) => (
              <PoliticallyExposedPersonForm
                key={i}
                index={i}
                remove={fields.length && fields.length > 1 ?
                  () => {
                    if (field.id) {
                      deleteField(field.id).then(() => fields.remove(i)).catch(e => console.log(e));
                    } else {
                      fields.remove(i);
                    }
                  } : null}
                kind={field.kind}
              />
            ))}
          </div>
          <Button
            className="u-mt-md"
            i18key="controls.buttons.add"
            id="btn_add_pep"
            onClick={() => fields.push({ ...emptyPep })}
            leftSymbolId="plus"
          />
        </>
      )}
    </FieldArray>
  );
};

export default PoliticallyExposedPersonsFields;
